import React from 'react';
import { Field, inputCls } from './FormKit';
import {
  YesNo, usePerCompany, PerCompanyRows, PerCompanyToggle, ariaFor, profileGroupByKey, profileSplitGaps,
} from './EngagementFormFields';
import { DetailGrid, DetailItem } from '../workflow/SectionCard';
import { ProfilePerCompany } from '../workflow/ProfileDetail';
import { groupSplit } from '../../utils/engagementProfile';
import ScreeningTnaReference from './ScreeningTnaReference';
import { TNE_LABEL, TNE_BASIS_LABEL, TNA_LABEL, TNA_BASIS_LABEL } from '../../pages/qcQuestions';

/**
 * The six screening questions — ONE definition for every surface that asks or
 * shows them (docs/modules/workflow-screening.md):
 *
 *   Engagement Registration   New / edit page — asked only while "Does QM
 *                             need to perform screening?" = Yes; Detail shows
 *                             the answers
 *   Screening (standalone)    New / Modify modal — always asked; Detail shows
 *   Management                Screening Records — New / Edit / Detail
 *
 * The answers travel from the registration onto the Screening request its
 * approval opens and, on completion, onto the screening record. They are never
 * written to engagements; the TNE / TNA pairs start the QC6 / QC7 form of the
 * engagements the registration created (editable there).
 *
 * A surface covering several companies answers each TNE / TNA pair per
 * company ("Assign per entity", the same control as the client profile's
 * questions — src/utils/engagementProfile.js holds the groups). Ultimate
 * Parent HQ City and the UBO are always shared.
 */

export const emptyScreeningAnswers = () => ({
  ultimateParentHqCity: '', ubo: '', isTne: '', tneBasis: '', isTna: '', tnaBasis: '',
});

const triToStr = (v) => (v === true ? 'yes' : v === false ? 'no' : '');

/** Server row (booleans / nulls) → form strings. */
export const screeningAnswersToFields = (r) => ({
  ultimateParentHqCity: (r && r.ultimateParentHqCity) || '',
  ubo: (r && r.ubo) || '',
  isTne: triToStr(r ? r.isTne : null),
  tneBasis: (r && r.tneBasis) || '',
  isTna: triToStr(r ? r.isTna : null),
  tnaBasis: (r && r.tnaBasis) || '',
});

/** Labels — the same wording as the QC6/QC7 form for the TNE / TNA pair. */
export const SCREENING_LABELS = {
  ultimateParentHqCity: 'Ultimate Parent HQ City',
  ubo: 'UBO',
  isTne: TNE_LABEL,
  tneBasis: TNE_BASIS_LABEL,
  isTna: TNA_LABEL,
  tnaBasis: TNA_BASIS_LABEL,
};

/**
 * Required-field gaps `[{ key, label }]` — every one is mandatory. A pair the
 * companies answer on their own is checked company by company instead
 * (profileSplitGaps), so the shared answer is not asked for.
 */
export const screeningRequiredGaps = (f, anchor = (k) => k, { companies = [] } = {}) => {
  const gaps = [];
  const gap = (key) => gaps.push({ key: anchor(key), label: SCREENING_LABELS[key] });
  const split = (f.profileSplit && typeof f.profileSplit === 'object') ? f.profileSplit : {};
  const perCompany = (key) => Object.keys(split[key] || {}).length > 0;
  if (!String(f.ultimateParentHqCity || '').trim()) gap('ultimateParentHqCity');
  if (!String(f.ubo || '').trim()) gap('ubo');
  if (!perCompany('isTne')) {
    if (f.isTne === '') gap('isTne');
    if (!String(f.tneBasis || '').trim()) gap('tneBasis');
  }
  if (!perCompany('isTna')) {
    if (f.isTna === '') gap('isTna');
    if (!String(f.tnaBasis || '').trim()) gap('tnaBasis');
  }
  profileSplitGaps(f, { companies: companies.length > 0 ? companies : null, fields: ['tne', 'tna'] })
    .forEach((g) => gaps.push({ ...g, key: anchor(g.key) }));
  return gaps;
};

/** Append the six answers to a FormData (POST / PATCH bodies). */
export const appendScreeningAnswers = (form, f) => {
  form.set('ultimateParentHqCity', f.ultimateParentHqCity || '');
  form.set('ubo', f.ubo || '');
  form.set('isTne', f.isTne === 'yes' ? 'true' : f.isTne === 'no' ? 'false' : '');
  form.set('tneBasis', f.tneBasis || '');
  form.set('isTna', f.isTna === 'yes' ? 'true' : f.isTna === 'no' ? 'false' : '');
  form.set('tnaBasis', f.tnaBasis || '');
  return form;
};

const noAnchor = () => null;

/**
 * One transnational assessment — the Yes/No and the basis it is founded on,
 * which split together: "Assign per entity" turns the pair into one row per
 * company (its Yes/No, its own basis under it), exactly as the client
 * profile's questions do. Collapsed, the basis is a field of its own.
 */
const AssessmentPair = ({ f, set, required, anchor, companies, groupKey, flagKey, basisKey }) => {
  const pc = usePerCompany(f, set, profileGroupByKey(groupKey), companies);
  const yesNo = (id) => <YesNo value={pc.get(flagKey, id)} onChange={(v) => pc.setv(flagKey, v, id)} />;
  const basis = (id) => (
    <textarea
      rows="2" value={pc.get(basisKey, id) || ''} onChange={(e) => pc.setv(basisKey, e.target.value, id)}
      className={inputCls} aria-label={ariaFor(SCREENING_LABELS[basisKey], id, companies)}
    />
  );
  return (
    <>
      <Field label={SCREENING_LABELS[flagKey]} required={required} field={anchor(flagKey)}>
        {pc.open ? (
          <Field field={anchor(`${groupKey}Split`)}>
            <PerCompanyRows companies={companies} render={(c) => (
              <div className="space-y-2">
                {yesNo(c.id)}
                <Field label={SCREENING_LABELS[basisKey]} required={required}>{basis(c.id)}</Field>
              </div>
            )} />
          </Field>
        ) : yesNo(undefined)}
        <PerCompanyToggle pc={pc} />
      </Field>
      {!pc.open && (
        <Field label={SCREENING_LABELS[basisKey]} required={required} field={anchor(basisKey)}>
          {basis(undefined)}
        </Field>
      )}
    </>
  );
};

/**
 * The six controls, in form order. `required` off only for read-mostly
 * editors; `companies` ([{ id, name }]) are the ticked companies of a
 * several-company form — with two or more, each TNE / TNA pair offers
 * "Assign per entity".
 */
export const ScreeningQuestionFields = ({ f, set, required = true, anchor = noAnchor, companies = [] }) => (
  <>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label={SCREENING_LABELS.ultimateParentHqCity} required={required} field={anchor('ultimateParentHqCity')}>
        <input type="text" value={f.ultimateParentHqCity} onChange={(e) => set('ultimateParentHqCity', e.target.value)} className={inputCls} />
      </Field>
      <Field label={SCREENING_LABELS.ubo} required={required} field={anchor('ubo')}>
        <input type="text" value={f.ubo} onChange={(e) => set('ubo', e.target.value)} className={inputCls} />
      </Field>
    </div>
    <AssessmentPair f={f} set={set} required={required} anchor={anchor} companies={companies} groupKey="tne" flagKey="isTne" basisKey="tneBasis" />
    <AssessmentPair f={f} set={set} required={required} anchor={anchor} companies={companies} groupKey="tna" flagKey="isTna" basisKey="tnaBasis" />
    {/* 判斷準則站在最後一題底下——四題 TNE／TNA 都答完才是需要對照的時候。 */}
    <ScreeningTnaReference />
  </>
);

const yesNo = (v) => (v === true ? 'Yes' : v === false ? 'No' : '—');

/**
 * One transnational assessment, read-only: the answer and its basis, or —
 * where the companies answered it on their own — one row per company
 * (the comment anchor is then `<group>Split`, the History row's key).
 */
const AssessmentView = ({ r, anchor, groupKey, flagKey, basisKey }) => {
  if (groupSplit(r.entities || [], profileGroupByKey(groupKey))) {
    return (
      <DetailItem label={`${SCREENING_LABELS[flagKey]} per Company`} field={anchor(`${groupKey}Split`)}>
        <ProfilePerCompany request={r} groupKey={groupKey} />
      </DetailItem>
    );
  }
  return (
    <>
      <DetailItem label={SCREENING_LABELS[flagKey]} field={anchor(flagKey)}>{yesNo(r[flagKey])}</DetailItem>
      <DetailItem label={SCREENING_LABELS[basisKey]} field={anchor(basisKey)}>
        <span className="whitespace-pre-wrap">{r[basisKey] || '—'}</span>
      </DetailItem>
    </>
  );
};

/** Read-only answers (Detail pages, Management record page). `anchor` maps
 *  keys to the page's comment keys; omit where there is no comment layer. */
export const ScreeningAnswersView = ({ r, anchor = (k) => k }) => (
  <DetailGrid>
    <DetailItem label={SCREENING_LABELS.ultimateParentHqCity} field={anchor('ultimateParentHqCity')}>{r.ultimateParentHqCity || '—'}</DetailItem>
    <DetailItem label={SCREENING_LABELS.ubo} field={anchor('ubo')}>{r.ubo || '—'}</DetailItem>
    <AssessmentView r={r} anchor={anchor} groupKey="tne" flagKey="isTne" basisKey="tneBasis" />
    <AssessmentView r={r} anchor={anchor} groupKey="tna" flagKey="isTna" basisKey="tnaBasis" />
  </DetailGrid>
);
